import { useState, useEffect, Suspense } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { ProfileHeader } from '@/components/profile/profile-header';
import { ExperienceSection } from '@/components/profile/experience-section';
import { EducationSection } from '@/components/profile/education-section';
import { ProjectsSection } from '@/components/profile/projects/projects-section';
import { ProfileEnhancement } from '@/components/profile/profile-enhancement';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Sparkles, AlertTriangle, ArrowLeft, Plus } from 'lucide-react';
import ExperienceForm from '@/components/dashboard/experience-form';

export default function ProfilePage() {
  const { id, customUrl } = useParams<{ id?: string; customUrl?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState<any>(null);
  const [experiences, setExperiences] = useState<any[]>([]);
  const [education, setEducation] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showExperienceForm, setShowExperienceForm] = useState(false);

  // Check if we're coming from the search page
  const fromSearch = location.state?.fromSearch || false;

  // State for collapsible sections
  const [openSections, setOpenSections] = useState({
    summary: true,
    experience: true,
    education: true,
    projects: true,
  });

  // Check if we're coming from the applicants list
  const fromApplicants = location.state?.fromApplicants || false;

  // Function to check if user can edit profile
  const canEdit = () => {
    return user?.id === profile?.id;
  };

  // Function to check connection status
  const checkConnection = async () => {
    if (!user || !profile?.id) return false;
    try {
      const { data, error } = await supabase
        .from('profile_connections')
        .select('status')
        .or(`and(requester_id.eq.${user.id},receiver_id.eq.${profile.id}),and(requester_id.eq.${profile.id},receiver_id.eq.${user.id})`)
        .eq('status', 'accepted')
        .single();
      if (error) throw error;
      return !!data;
    } catch (error) {
      console.error('Error checking connection:', error);
      return false;
    }
  };

  // Subscribe to profile changes
  useEffect(() => {
    if (!profile?.id) return;

    const channel = supabase
      .channel(`profile-${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${profile.id}`
        },
        async () => {
          // When profile changes, fetch fresh data
          await fetchProfile();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  // Fetch profile data
  const fetchProfile = async () => {
    setLoading(true);
    setError(null);

    try {
      let profileData;
      let profileError;

      // First try to find by custom URL if provided
      if (customUrl) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('custom_url', customUrl)
          .single();
        
        profileData = data;
        profileError = error;
      } 
      // Then try by ID
      else if (id) {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', id)
          .single();
        
        profileData = data;
        profileError = error;
      } 
      else {
        throw new Error('No profile identifier provided');
      }

      if (profileError || !profileData) {
        throw new Error('Profile not found');
      }

      // Check profile visibility
      const isOwnProfile = user?.id === profileData.id;
      const canViewProfile =
        isOwnProfile ||
        profileData.privacy_level === 'public' ||
        (profileData.privacy_level === 'connections' && await checkConnection());

      if (!canViewProfile) {
        throw new Error(
          profileData.privacy_level === 'connections'
            ? 'This profile is only visible to connections'
            : 'This profile is private'
        );
      }

      // Fetch related data
      const [
        { data: experiencesData, error: experiencesError },
        { data: educationData, error: educationError },
        { data: projectsData, error: projectsError }
      ] = await Promise.all([
        supabase
          .from('experiences')
          .select('*')
          .eq('profile_id', profileData.id)
          .order('start_date', { ascending: false }),
        supabase
          .from('education')
          .select('*')
          .eq('profile_id', profileData.id)
          .order('start_date', { ascending: false }),
        supabase
          .from('projects')
          .select('*')
          .eq('profile_id', profileData.id)
          .order('created_at', { ascending: false })
      ]);

      if (experiencesError) throw experiencesError;
      if (educationError) throw educationError;
      if (projectsError) throw projectsError;

      // Update state
      setProfile(profileData);
      setExperiences(experiencesData || []);
      setEducation(educationData || []);
      setProjects(projectsData || []);

      // If viewing as recruiter from applicants, redirect to evaluation view
      if (fromApplicants) {
        navigate(`/evaluate/${profileData.id}`, {
          state: { ...location.state, profile: profileData },
        });
        return;
      }
    } catch (err: any) {
      console.error('Profile page error:', {
        error: err.message,
        userId: id,
        customUrl,
        context: 'Profile page load',
        timestamp: new Date().toISOString(),
      });
      setError(err.message);
      if (err.message === 'Profile not found') {
        toast({
          variant: 'destructive',
          title: 'Profile Not Found',
          description: 'This profile does not exist or has been deleted',
        });
        navigate('/dashboard');
      } else {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: err.message || 'Failed to load profile',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchProfile();
  }, [id, customUrl]);

  const toggleSection = (section: keyof typeof openSections) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const isProfileEmpty = profile && !profile.full_name && !profile.title && !profile.summary;

  if (loading) {
    return (
      <div className="flex-1 flex justify-center py-8 px-4 md:px-6 print:py-4 print:px-0">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="text-sm text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="mt-4 text-center">
          <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex-1 flex justify-center py-8 px-4 md:px-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Profile Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The requested profile could not be found or may have been deleted.
          </p>
          <Button onClick={() => navigate('/dashboard')}>Return to Dashboard</Button>
        </div>
      </div>
    );
  }

  // Show onboarding card for empty profiles if the user is editing their own profile
  if (isProfileEmpty && canEdit()) {
    return (
      <div className="flex-1 flex items-center justify-center py-8 px-4 md:px-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-6">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-primary/10 p-3">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center mb-2">Complete Your Profile</h2>
            <p className="text-muted-foreground text-center mb-6">
              Take a few minutes to set up your professional profile and start getting matched with opportunities.
            </p>
            <Button className="w-full" size="lg" onClick={() => navigate('/onboarding')}>
              Start Profile Setup
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container-narrow py-8 px-4 md:px-6">
      <div className="space-y-6">
        {fromSearch && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/search')}
            className="mb-6 -ml-2"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Search
          </Button>
        )}

        {/* Profile Enhancement Progress */}
        {canEdit() && (
          <div className="mb-6">
            <ProfileEnhancement 
              profile={profile}
              experiences={experiences}
              education={education}
              projects={projects}
            />
          </div>
        )}

        {/* Profile Content */}
        <div className="bg-card border rounded-lg shadow-lg overflow-hidden mb-8">
          <div className="p-6 md:p-8">
            <ProfileHeader 
              isEditing={canEdit()} 
              profileData={profile} 
              isPublicView={!canEdit()} 
            />
          </div>
        </div>

        {/* Lazy-loaded Experience Section */}
        <Suspense fallback={<p>Loading work experience...</p>}>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Work Experience</h2>
              {canEdit() && (
                <Button onClick={() => setShowExperienceForm(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Experience
                </Button>
              )}
            </div>
            
            {/* Inline Experience Form */}
            {showExperienceForm && (
              <ExperienceForm
                open={true}
                onOpenChange={() => setShowExperienceForm(false)}
                onSuccess={() => {
                  setShowExperienceForm(false);
                  fetchProfile();
                }}
                mode="inline"
              />
            )}
            
            <ExperienceSection isEditing={canEdit()} />
          </div>
        </Suspense>

        <Separator className="my-8" />

        {/* Lazy-loaded Education Section */}
        <Suspense fallback={<p>Loading education...</p>}>
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">Education</h2>
            <EducationSection isEditing={canEdit()} />
          </div>
        </Suspense>

        <Separator className="my-8" />

        {/* Lazy-loaded Projects Section */}
        <Suspense fallback={<p>Loading projects...</p>}>
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-4">Projects</h2>
            <ProjectsSection isEditing={canEdit()} />
          </div>
        </Suspense>
      </div>
    </div>
  );
}